import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import english from '../../data/homeCopy/en.json';
import { SITE_LANGUAGE_CODES } from '../../data/siteLanguages';
import { chartHref } from '../../data/chartLanguage';
import { noteBannerEvent } from '../../services/chartService';
import { readSavedChart } from '../../services/savedChart';

const ANNOUNCE_KEY = 'nowtarot_chart_banner_closed';
const QUIET_PATHS = ['/account', '/support', '/wallet', '/home', '/llm', '/translate'];

function onPath(pathname, path) {
  return pathname === path || pathname === `${path}/` || pathname.startsWith(`${path}/`);
}

const COPY_LOADERS = {
  es: () => import('../../data/homeCopy/es.json'),
  hi: () => import('../../data/homeCopy/hi.json'),
  zh: () => import('../../data/homeCopy/zh.json'),
  ar: () => import('../../data/homeCopy/ar.json'),
  fr: () => import('../../data/homeCopy/fr.json'),
  pt: () => import('../../data/homeCopy/pt.json'),
  de: () => import('../../data/homeCopy/de.json'),
  ja: () => import('../../data/homeCopy/ja.json'),
  ko: () => import('../../data/homeCopy/ko.json'),
  it: () => import('../../data/homeCopy/it.json'),
  tr: () => import('../../data/homeCopy/tr.json'),
  id: () => import('../../data/homeCopy/id.json'),
  pl: () => import('../../data/homeCopy/pl.json'),
  vi: () => import('../../data/homeCopy/vi.json'),
  th: () => import('../../data/homeCopy/th.json'),
  nl: () => import('../../data/homeCopy/nl.json'),
  ru: () => import('../../data/homeCopy/ru.json'),
  fil: () => import('../../data/homeCopy/fil.json'),
  uk: () => import('../../data/homeCopy/uk.json'),
  ro: () => import('../../data/homeCopy/ro.json'),
  sv: () => import('../../data/homeCopy/sv.json'),
  cs: () => import('../../data/homeCopy/cs.json'),
  el: () => import('../../data/homeCopy/el.json'),
  hu: () => import('../../data/homeCopy/hu.json'),
};

function pageLanguage(pathname, search) {
  const query = new URLSearchParams(search || '').get('lang');
  if (query && SITE_LANGUAGE_CODES.includes(query)) return query;
  const code = pathname.split('/')[1];
  return SITE_LANGUAGE_CODES.includes(code) ? code : 'en';
}

const ProviderBanner = () => {
  const [message, setMessage] = useState(null);
  const [choice, setChoice] = useState(null);
  const viewed = useRef(false);
  const { pathname, search } = useLocation();
  const lang = pageLanguage(pathname, search);
  const [announceCopy, setAnnounceCopy] = useState(lang === 'en' ? english : null);

  useEffect(() => {
    if (lang === 'en') {
      setAnnounceCopy(english);
      return undefined;
    }
    let live = true;
    setAnnounceCopy(null);
    const loader = COPY_LOADERS[lang];
    if (!loader) return undefined;
    loader()
      .then((mod) => {
        const copy = mod.default || mod;
        if (live && copy.announce) setAnnounceCopy(copy);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [lang]);

  useLayoutEffect(() => {
    // Chosen after mount so prerendered pages hydrate without the banner.
    if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') return;
    let closed = false;
    try {
      closed = localStorage.getItem(ANNOUNCE_KEY) === '1';
    } catch {
      closed = false;
    }
    if (closed) return;
    const saved = Boolean(readSavedChart());
    setChoice({ variant: saved ? 'daily' : 'generate', saved });
  }, []);

  const closeAnnounce = () => {
    setChoice(null);
    try {
      localStorage.setItem(ANNOUNCE_KEY, '1');
    } catch {
      // Private mode can block storage.
    }
  };

  const pack = announceCopy?.announceChart || english.announceChart;
  const text = (choice && (pack[choice.variant] || english.announceChart[choice.variant]))
    || english.announceChart.generate;
  const badge = announceCopy?.announce?.badge || english.announce.badge;
  const closeLabel = announceCopy?.announce?.close || english.announce.close;
  const onDestination = choice?.saved
    ? onPath(pathname, '/personalized-daily-horoscope')
    : onPath(pathname, '/birth-chart');
  const showAnnounce = !message && choice && announceCopy && !QUIET_PATHS.includes(pathname) && !onDestination;
  const shown = message ? 'provider' : (showAnnounce ? 'announce' : null);
  const destination = chartHref(
    choice?.saved ? '/personalized-daily-horoscope/' : '/birth-chart/',
    lang,
  );

  useEffect(() => {
    let cancelled = false;
    let hideTimer = null;

    const apply = (data) => {
      if (cancelled) return;
      const show = Boolean(data && data.banner && data.message);
      setMessage(show ? data.message : null);
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
      if (!show || !data.until) return;
      const delay = new Date(data.until).getTime() - Date.now();
      if (delay > 0 && delay < 12 * 60 * 60 * 1000) {
        hideTimer = setTimeout(() => setMessage(null), delay);
      }
    };

    const load = () => {
      const controller = new AbortController();
      const kill = setTimeout(() => controller.abort(), 4000);
      fetch('/api/v1/status/provider', { cache: 'no-store', signal: controller.signal })
        .then((response) => (response.ok ? response.json() : null))
        .then(apply)
        .catch(() => {})
        .finally(() => clearTimeout(kill));
    };

    load();
    const poll = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(poll);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (!shown) {
      root.style.setProperty('--site-banner-height', '0px');
      root.classList.remove('provider-banner-on');
      return undefined;
    }

    const banner = document.getElementById('provider-banner');
    const setHeight = () => {
      const height = banner ? banner.offsetHeight : 0;
      root.style.setProperty('--site-banner-height', `${height}px`);
      root.classList.add('provider-banner-on');
    };
    setHeight();
    const observer = banner && typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(setHeight)
      : null;
    if (observer && banner) observer.observe(banner);
    return () => {
      if (observer) observer.disconnect();
      root.style.setProperty('--site-banner-height', '0px');
      root.classList.remove('provider-banner-on');
    };
  }, [shown, message, announceCopy]);

  useEffect(() => {
    if (viewed.current || shown !== 'announce' || !choice) return;
    viewed.current = true;
    noteBannerEvent(choice.variant, 'view', lang);
  }, [shown, choice, lang]);

  if (shown === 'announce') {
    return (
      <div className="provider-banner announce-banner" id="provider-banner" dir={lang === 'ar' ? 'rtl' : undefined}>
        <Link
          to={destination}
          className="announce-main"
          onClick={() => noteBannerEvent(choice.variant, 'click', lang)}
        >
          {!choice.saved && <span className="announce-new">{badge}</span>}
          <span className="announce-spark" aria-hidden="true">✨</span>
          <span className="announce-long">{text.long}</span>
          <span className="announce-short">{text.short}</span>
          <span className="announce-link">{text.cta}</span>
        </Link>
        <button type="button" className="announce-close" aria-label={closeLabel} onClick={closeAnnounce}>×</button>
      </div>
    );
  }

  if (!message) return null;

  return (
    <div className="provider-banner" id="provider-banner" role="status">
      {message}
    </div>
  );
};

export default ProviderBanner;
